import React, { useEffect, useState } from "react";
import Modal from "../common/modal";
import Button from "../common/button";
import "../../assets/styles/table.css";
import {
  APPLICATION_IMAGE_STATUSES,
  getApplicationImageStatus,
  getApplicationImageStatusClass,
} from "../../utils/applicationImageStatus";
import { getRejectReason } from "../../utils/applicationImageRejectReason";

const ApplicationImageStatusModal = ({
  isOpen,
  onClose,
  image,
  onSave,
  isLoading = false,
  canApprove = true,
  approveLimitMessage = "",
}) => {
  const [status, setStatus] = useState("pending");
  const [rejectReason, setRejectReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  useEffect(() => {
    if (image && isOpen) {
      setStatus(getApplicationImageStatus(image));
      setRejectReason(getRejectReason(image));
      setReasonError("");
    }
  }, [image, isOpen]);

  if (!isOpen) return null;

  const title = image?.title || image?.name || "Application Image";
  const currentStatus = getApplicationImageStatus(image);
  const statusClass = getApplicationImageStatusClass(status);
  const isRejecting = status === "rejected";
  const canSelectApproved = canApprove || currentStatus === "approved";
  const canSave =
    !isLoading && (status !== "approved" || canSelectApproved);

  const handleSave = () => {
    if (status === "approved" && !canSelectApproved) {
      setReasonError(
        approveLimitMessage ||
          "Approved application image limit reached. Upgrade your plan to approve more.",
      );
      return;
    }
    if (isRejecting) {
      const trimmed = rejectReason.trim();
      if (!trimmed) {
        setReasonError("Please provide a reason for rejection.");
        return;
      }
      onSave(status, trimmed);
      return;
    }
    onSave(status);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Status">
      <div style={{ padding: "4px 0 16px" }}>
        <p style={{ margin: "0 0 16px", color: "#555", fontSize: "14px" }}>
          <strong style={{ color: "#333" }}>{title}</strong>
        </p>

        <label
          htmlFor="application-image-status"
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: "13px",
            fontWeight: 600,
            color: "#555",
          }}
        >
          Status
        </label>
        <select
          id="application-image-status"
          className={`status-select ${statusClass}`}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            if (e.target.value !== "rejected") {
              setReasonError("");
            }
          }}
          disabled={isLoading}
        >
          {APPLICATION_IMAGE_STATUSES.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.value === "approved" && !canSelectApproved}
            >
              {option.label}
            </option>
          ))}
        </select>

        {status === "approved" && !canSelectApproved && (
          <p style={{ margin: "10px 0 0", color: "#8c1d18", fontSize: "12px" }}>
            {approveLimitMessage ||
              "Approved application image limit reached. You can still set Pending or Rejected."}
          </p>
        )}

        {isRejecting && (
          <div style={{ marginTop: "16px" }}>
            <label
              htmlFor="application-image-reject-reason"
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#555",
              }}
            >
              Rejection reason <span style={{ color: "#d11a2a" }}>*</span>
            </label>
            <textarea
              id="application-image-reject-reason"
              rows={4}
              value={rejectReason}
              onChange={(e) => {
                setRejectReason(e.target.value);
                if (reasonError) setReasonError("");
              }}
              placeholder="Describe why this application image is being rejected..."
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: reasonError ? "1px solid #d11a2a" : "1px solid #ddd",
                fontSize: "14px",
                fontFamily: "inherit",
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
            {reasonError && (
              <p style={{ margin: "8px 0 0", color: "#d11a2a", fontSize: "12px" }}>
                {reasonError}
              </p>
            )}
          </div>
        )}

        <p style={{ margin: "10px 0 0", fontSize: "12px", color: "#888" }}>
          You can save Pending, Approved, or Rejected. Only Approved uses your
          plan quota.
        </p>
      </div>

      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Button onClick={onClose} className="CancelButton" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="CommonButton"
          disabled={!canSave}
        >
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </div>
    </Modal>
  );
};

export default ApplicationImageStatusModal;
